import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAssignAreaList,
  saveUserRegion,
  useCompanyList,
  NOTICE_QUERY_KEYS,
  deleteUserRegion,
} from "~/services/customer/home";
import useOpenModal from "^/common/useOpenModal";

export interface AssignAreaListType {
  latitude: string;
  longitude: string;
  regionAddr: string;
  regionName: string;
  regionSeq: number;
  userSeq: number;
  region: string;
}

export interface SearchAreaType {
  address_name: string;
  address_type: "ROAD" | "REGION";
  y: string;
  x: string;
  road_address: null | {
    address_name: string;
    building_name: string;
    main_building_no: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_name: string;
    road_name: string;
    sub_building_no: string;
    underground_yn: string;
    x: string;
    y: string;
    zone_no: string;
  };
  address: null | {
    address_name: string;
    b_code: string;
    h_code: string;
    main_address_no: string;
    mountain_yn: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_h_name: string;
    region_3depth_name: string;
    sub_address_no: string;
    x: string;
    y: string;
  };
}

export interface Address {
  address_name: string;
  b_code: string;
  h_code: string;
  main_address_no: string;
  mountain_yn: string;
  region_1depth_name: string;
  region_2depth_name: string;
  region_3depth_h_name: string;
  region_3depth_name: string;
  sub_address_no: string;
  x: string;
  y: string;
}

const useStoreSearch = () => {
  const queryClient = useQueryClient();
  const {
    data: assignAreaList,
    isLoading: isAssignAreaLoading,
    isFetching: isAssignAreaFetching,
  } = useAssignAreaList();
  const [selectedArea, setSelectedArea] = useState<AssignAreaListType>();
  const [selectedSearchTypeFilter, setSelectedSearchTypeFilter] = useState<{
    label: string;
    value: string | null;
  }>({ label: "전체", value: null });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchArea, setSearchArea] = useState<SearchAreaType | null>(null);
  const [loading, setLoading] = useState(false);

  // 업체 목록 조회
  const {
    data: companyList,
    isLoading: isCompanyLoading,
    isFetching: isCompanyFetching,
  } = useCompanyList(
    {
      region: selectedArea?.region || "",
      category: selectedSearchTypeFilter.value,
    },
    !!selectedArea && !!selectedArea.region
  );

  // 모달 관련 상태
  const { isOpenModal, clickModal, closeModal } = useOpenModal();
  const {
    isOpenModal: isOpenAddAreaModal,
    clickModal: clickAddAreaModal,
    closeModal: closeAddAreaModal,
  } = useOpenModal();

  // 동 검색 안내 Dialog 상태
  const {
    isOpenModal: isOpenDongDialog,
    clickModal: openDongDialog,
    closeModal: closeDongDialog,
  } = useOpenModal();

  // 알림 Dialog 상태들
  const {
    isOpenModal: isOpenAlertDialog,
    clickModal: openAlertDialog,
    closeModal: closeAlertDialog,
  } = useOpenModal();

  const [alertMessage, setAlertMessage] = useState("");
  const hasInitializedArea = useRef(false);

  // 알림 Dialog 열기 함수
  const showAlert = (message: string) => {
    setAlertMessage(message);
    openAlertDialog();
  };

  // 폼 관련
  const { handleSubmit, control } = useForm<{ area: string }>({
    defaultValues: {
      area: "",
    },
    mode: "onBlur",
  });

  // 필터 옵션
  const categorys = [
    { label: "전체", value: null },
    { label: "유치원", value: "PROVIDED_KINDERGARTEN" },
    { label: "데이케어", value: "PROVIDED_DAYCARE" },
    { label: "호텔서비스", value: "PROVIDED_HOTEL" },
    { label: "미용실", value: "PROVIDED_GROOMING" },
  ];

  useEffect(() => {
    if (assignAreaList && assignAreaList.length > 0 && !hasInitializedArea.current) {
      // 세션에서 저장된 지역 정보 가져오기
      const savedRegionSeq = sessionStorage.getItem("selectedRegionSeq");
      
      if (savedRegionSeq) {
        // 세션에 저장된 지역이 목록에 있는지 확인
        const savedArea = assignAreaList.find(
          (area: AssignAreaListType) => area.regionSeq === Number(savedRegionSeq)
        );
        
        if (savedArea) {
          // 세션에 저장된 지역이 목록에 있으면 선택
          setSelectedArea(savedArea);
        } else {
          // 세션에 저장된 지역이 목록에 없으면 첫 번째 항목 선택
          setSelectedArea(assignAreaList[0]);
          // 세션에서 제거
          sessionStorage.removeItem("selectedRegionSeq");
        }
      } else {
        // 세션에 저장된 값이 없으면 첫 번째 항목 선택
        setSelectedArea(assignAreaList[0]);
      }
      hasInitializedArea.current = true;
    }
    console.log(assignAreaList);
    if (assignAreaList && assignAreaList.length === 0) {
      setSelectedArea({
        latitude: "",
        longitude: "",
        regionAddr: "",
        regionName: "선택",
        regionSeq: 0,
        userSeq: 0,
        region: "",
      });
    }
  }, [assignAreaList]);

  const handleAreaClick = (area: AssignAreaListType) => {
    setSelectedArea(area);
  };

  const handleSearchTypeClick = (type: {
    label: string;
    value: string | null;
  }) => {
    setSelectedSearchTypeFilter(type);
  };

  const handleDropdownToggle = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleDropdownClose = () => {
    setDropdownOpen(false);
  };

  const handleAddArea = useCallback(() => {
    setDropdownOpen(false);
    if (assignAreaList && assignAreaList.length === 3) {
      clickModal();
    } else {
      clickAddAreaModal();
    }
  }, [assignAreaList, clickModal, clickAddAreaModal]);

  const handleAddOkClick = async () => {
    if (!searchArea) {
      showAlert("지역을 선택해주세요.");
      return;
    }

    if (loading) return;

    try {
      setLoading(true);
      console.log(searchArea);

      const regionData = {
        regionName: "",
        regionAddr: "",
        latitude: parseFloat(searchArea.y || "0"),
        longitude: parseFloat(searchArea.x || "0"),
      };

      if (searchArea.address_type === "ROAD") {
        regionData.regionName = searchArea.road_address?.road_name || "";
        regionData.regionAddr = searchArea.road_address?.address_name || "";
        regionData.latitude = parseFloat(searchArea.road_address?.y || "0");
        regionData.longitude = parseFloat(searchArea.road_address?.x || "0");
      } else if (searchArea.address_type === "REGION") {
        regionData.regionName = searchArea.address?.region_3depth_name || "";
        regionData.regionAddr = searchArea.address?.address_name || "";
        regionData.latitude = parseFloat(searchArea.address?.y || "0");
        regionData.longitude = parseFloat(searchArea.address?.x || "0");
      }

      await saveUserRegion(regionData);

      // 지역 목록 쿼리를 다시 호출
      await queryClient.invalidateQueries({
        queryKey: [...NOTICE_QUERY_KEYS.home, "assign-area"],
      });

      showAlert("지역이 성공적으로 저장되었습니다.");
      closeAddAreaModal();
      setSearchArea(null); // 검색 영역 초기화
    } catch (error) {
      console.error("지역 저장 실패:", error);
      showAlert("지역 저장에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const bindSubmit = async (data: { area: string }) => {
    try {
      const targetArea = assignAreaList?.find(
        (a: AssignAreaListType) => a.region === data.area
      );
      if (!targetArea) {
        showAlert("선택한 지역을 찾을 수 없습니다.");
        return;
      }
      await deleteUserRegion(targetArea);
      await queryClient.invalidateQueries({
        queryKey: [...NOTICE_QUERY_KEYS.home, "assign-area"],
      });
      showAlert("지역이 성공적으로 삭제되었습니다.");
      closeModal();
      clickAddAreaModal();
    } catch (e: unknown) {
      console.log(e);
      showAlert("지역 삭제에 실패했습니다.");
    }
  };

  const handleAreaSelection = (area: AssignAreaListType) => {
    handleAreaClick(area);
    // 세션에 선택한 지역 저장
    sessionStorage.setItem("selectedRegionSeq", String(area.regionSeq));
    handleDropdownClose();
  };

  return {
    // 기존 상태들
    assignAreaList,
    selectedArea,
    setSelectedArea,
    selectedSearchTypeFilter,
    categorys,

    // 업체 목록 관련
    companyList,
    isCompanyLoading,

    // 드롭다운 관련
    dropdownOpen,
    handleDropdownToggle,
    handleDropdownClose,
    handleAreaSelection,

    // 검색 관련
    handleSearchTypeClick,

    // 지역 추가 관련
    searchArea,
    setSearchArea,
    loading,
    handleAddArea,
    handleAddOkClick,

    // 모달 관련
    isOpenModal,
    clickModal,
    closeModal,
    isOpenAddAreaModal,
    clickAddAreaModal,
    closeAddAreaModal,

    // 동 검색 안내 Dialog 관련
    isOpenDongDialog,
    openDongDialog,
    closeDongDialog,

    // 알림 Dialog 관련
    isOpenAlertDialog,
    openAlertDialog,
    closeAlertDialog,
    alertMessage,
    showAlert,

    // 폼 관련
    handleSubmit,
    control,
    bindSubmit,

    // 로딩 상태
    isLoading: isAssignAreaLoading || isCompanyLoading,
    isFetching: isAssignAreaFetching || isCompanyFetching,
  };
};

export default useStoreSearch;
